/* eslint-disable react/require-default-props */
import React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {get, noop} from 'lodash';
import {capitalize} from 'lodash/fp';

import Avatar from '/component/base/Avatar';
import Button from '/component/base/Button';
import FormattedNumber from '/component/base/FormattedNumber';
import InlineFormattedNumber from '/component/base/FormattedNumber/InlineFormattedNumber';
import Icon from '/component/base/Icon';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import ScreenSection from '/component/layout/ScreenSection';
import {OrderDetails} from '/constant/TestIDs';
import {CURRENCY_USD} from '/enum/currency.enum';
import {EVENT_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {copyToClipboard} from '/util/clipboard.util';
import {generateCallback} from '/util/component.util';
import {generateComponentCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {styles as sectionStyles} from '../Section.style';

import {styles} from './OrderSection.style';

const normalizeAmount = (value: number) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getCurrencyAccessibilityValue = (value: number, currencyCode: string) =>
  `${value} ${currencyCode}`;

interface OrderSectionProps {
  tt?: (key: any, value: any) => any;
  orderType?: string;
  orderDate?: string;
  orderStatus?: string;
  orderCv?: string;
  orderQv?: string;
  orderCurrencyCode?: string;
  subtotalConsumable?: number;
  orderTotal?: number;
  customerIsCoach?: boolean;
  customerTypeId?: number;
  customerFullName?: string;
  customerActivatedOn?: string;
  customerAvatarUrl?: string;
  customerEmail?: string;
  onViewProfile?: () => void;
  onViewOrderHistory?: () => void;
  customerId?: number | null;
  lastPaidRank?: string;
}

export const OrderSection: React.FC<OrderSectionProps> = ({
  tt = withTranslation.ttDefaultProp,
  customerId = null,
  orderType = '',
  orderDate = '',
  orderStatus = '',
  orderCv = '',
  orderQv = '',
  orderCurrencyCode = '',
  subtotalConsumable = 0,
  orderTotal = 0,
  customerIsCoach = false,
  customerTypeId = 0,
  customerFullName = '',
  customerActivatedOn = '',
  customerAvatarUrl = '',
  customerEmail = '',
  onViewProfile = noop,
  onViewOrderHistory = noop,
  lastPaidRank = '',
}: OrderSectionProps) => {
  const dateUtil = getDateUtilDefaultInstance();
  const featureFlag = useSelector(variablesFeatureFlag);
  const updatePremierPlus = shouldReleaseFeature(
    get(featureFlag, 'ff_Premier_Plus', ''),
  );
  const shouldShowNewOrderHistory = shouldReleaseFeature(
    get(featureFlag, 'ff_order_history', ''),
  );
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.profileClick,
  });
  const generateOnViewProfilePress = generateCallback({
    func: sendEvent,
    params: {
      userType: USER_TYPES.client,
      userId: customerId,
      userFullName: customerFullName,
      lastPaidRank: lastPaidRank,
    },
  });

  const onViewProfilePress = () => {
    onViewProfile();
    generateOnViewProfilePress();
  };
  const isCoach =
    customerTypeId === 1
      ? true
      : customerTypeId === 2
      ? false
      : customerIsCoach;
  const orderTotalCurrencyCode = orderCurrencyCode || CURRENCY_USD;
  const subtotalConsumableDisplayValue = normalizeAmount(subtotalConsumable);
  const orderTotalDisplayValue = normalizeAmount(orderTotal);
  const hasOrderCv = Number.isFinite(Number(orderCv));
  const hasOrderQv = Number.isFinite(Number(orderQv));
  const orderStatusDisplayValue =
    orderStatus.trim() || (shouldShowNewOrderHistory ? '—' : '-');
  let updatedOrderType: string;

  if (!updatePremierPlus) {
    updatedOrderType =
      orderType.toLowerCase() === 'ondemand'
        ? 'on-demand'
        : capitalize(orderType);
  } else {
    if (orderType.toLowerCase() === 'premier') {
      updatedOrderType = 'Premier+';
    } else if (orderType.toLowerCase() === 'ondemand') {
      updatedOrderType = 'on-demand';
    } else {
      updatedOrderType = capitalize(orderType);
    }
  }

  const optaviaIdAccessibilityLabel = customerId
    ? `Copy OPTAVIA ID ${customerId}`
    : '';

  return (
    <ScreenSection
      style={{
        ...sectionStyles,
        heading: shouldShowNewOrderHistory
          ? styles.sectionHeading
          : {paddingTop: 0},
        content: styles.base,
      }}
      title={generateComponentCallback({
        component: (
          <Gutter>
            <Text
              variant="subheading"
              emphasis="low"
              testID={OrderDetails.orderSectionHeading}
              accessible={true}
              accessibilityRole="header">
              {tt('heading', 'Order Details')}
            </Text>
          </Gutter>
        ),
      })}>
      <Gutter>
        <View
          style={styles.profile}
          {...testProperties(OrderDetails.clientDetailsSection)}>
          <View style={styles.profileAvatar}>
            <Avatar
              size={80}
              avatarUrl={customerAvatarUrl}
              avatarKey={customerEmail}
              isCoach={isCoach}
              accessible={true}
              accessibilityRole="image"
              fullName={customerFullName}
              testID={OrderDetails.profileAvatar}
            />
          </View>
          <View style={styles.profileInfo}>
            <Text
              variant="small"
              emphasis="dull"
              testID={OrderDetails.profileType}
              accessible={true}
              accessibilityRole="text">
              {isCoach
                ? tt('optaviaCoach', 'OPTAVIA Coach')
                : tt('optaviaClient', 'OPTAVIA Client')}
            </Text>
            <Text
              variant="button"
              testID={OrderDetails.profileName}
              accessible={true}
              accessibilityRole="text">
              {customerFullName}
            </Text>
            {customerActivatedOn
              ? tt(
                  'clientSince',
                  generateComponentCallback({
                    component: (
                      <Text
                        testID={OrderDetails.clientSince}
                        accessible={true}
                        accessibilityRole="text">
                        Client since{' '}
                        {dateUtil.formatDate(
                          customerActivatedOn,
                          'MMMM d, yyyy',
                        )}
                      </Text>
                    ),
                  }),
                )
              : null}

            {customerId && (
              <View
                accessible={false}
                accessibilityLabel={`OPTAVIA ID : ${customerId}`}
                style={styles.optaviaIdContainer}
                {...testProperties(OrderDetails.optaviaId)}>
                <Text
                  variant="heading"
                  emphasis="dull"
                  accessible={true}
                  testID={OrderDetails.optaviaIdPrefix}>
                  OPTA
                </Text>
                <Text
                  variant="small"
                  emphasis="dull"
                  accessible={true}
                  testID={OrderDetails.optaviaIdLabel}>
                  VIA ID
                </Text>
                <TouchableOpacity
                  testID={OrderDetails.optaviaIdCopyButton}
                  style={styles.optaviaIdValueContainer}
                  accessible={true}
                  focusable={true}
                  accessibilityRole="button"
                  accessibilityLabel={optaviaIdAccessibilityLabel}
                  onPress={generateCallback({
                    func: copyToClipboard,
                    params: customerId.toString(),
                  })}>
                  <Text
                    variant="heading"
                    useCompetency={true}
                    testID={OrderDetails.optaviaIdValue}
                    accessible={true}
                    style={styles.optaviaIdValue}>
                    {customerId}
                  </Text>
                  <Icon
                    style={styles.copyIcon}
                    name="copy"
                    testID={OrderDetails.optaviaIdCopyIcon}
                    accessible={false}
                  />
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
        <View style={styles.viewProfile}>
          <Button
            testID={OrderDetails.viewProfileButton}
            labelTestID={OrderDetails.viewProfileButtonLabel}
            onPress={onViewProfilePress}
            accessible={true}
            accessibilityLabel={tt('viewProfile', 'View Profile')}
            accessibilityRole="button"
            accessibilityHint={`Opens profile for ${customerFullName}`}
            style={{
              base: {...styles.button, ...styles.viewProfileButton},
              text: styles.buttonText,
            }}>
            {tt('viewProfile', 'View Profile')}
          </Button>
          <Button
            type="secondary"
            testID={OrderDetails.viewOrderHistoryButton}
            labelTestID={OrderDetails.viewOrderHistoryButtonLabel}
            onPress={onViewOrderHistory}
            accessible={true}
            accessibilityLabel={tt('viewOrderHistory', 'View Order History')}
            focusable={true}
            style={{base: styles.button, text: styles.buttonText}}>
            {tt('viewOrderHistory', 'View Order History')}
          </Button>
        </View>
        <View style={styles.stats}>
          <ListItem
            testID={OrderDetails.cv}
            readonly
            style={{base: styles.statItem}}
            accessible={false}
            accessibilityLabel={`${
              tt('stats.cv.a11yLabel', 'Commissionable Volume') ||
              tt('stats.cv.label', 'CV')
            }: ${hasOrderCv ? orderCv : '—'}`}
            label={
              <Text testID={OrderDetails.cvLabel} accessible={true}>
                {tt('stats.cv.label', 'CV')}
              </Text>
            }
            suffix={
              hasOrderCv ? (
                <InlineFormattedNumber
                  testID={OrderDetails.cvValue}
                  value={orderCv}
                  decimalPlaces={2}
                  accessible={true}
                />
              ) : (
                <Text
                  variant="button"
                  testID={OrderDetails.cvValue}
                  accessible={true}>
                  —
                </Text>
              )
            }
          />
          <ListItem
            testID={OrderDetails.qv}
            readonly
            style={{base: styles.statItem}}
            accessible={false}
            accessibilityLabel={`${
              tt('stats.qv.a11yLabel', 'Qualifying Volume') ||
              tt('stats.qv.label', 'QV')
            }: ${hasOrderQv ? orderQv : '—'}`}
            label={
              <Text testID={OrderDetails.qvLabel} accessible={true}>
                {tt('stats.qv.label', 'QV')}
              </Text>
            }
            suffix={
              hasOrderQv ? (
                <InlineFormattedNumber
                  testID={OrderDetails.qvValue}
                  value={orderQv}
                  decimalPlaces={2}
                  accessible={true}
                />
              ) : (
                <Text
                  variant="button"
                  testID={OrderDetails.qvValue}
                  accessible={true}>
                  —
                </Text>
              )
            }
          />
          <ListItem
            testID={OrderDetails.orderType}
            readonly
            style={{base: styles.statItem}}
            accessible={false}
            accessibilityLabel={`${tt(
              'stats.orderType.label',
              'Order Type',
            )}: ${updatedOrderType}`}
            label={
              <Text testID={OrderDetails.orderTypeLabel} accessible={true}>
                {tt('stats.orderType.label', 'Order Type')}
              </Text>
            }
            suffix={
              <Text
                variant="button"
                testID={OrderDetails.orderTypeValue}
                accessible={true}>
                {updatedOrderType}
              </Text>
            }
          />
          <ListItem
            testID={OrderDetails.orderDate}
            readonly
            style={{base: styles.statItem}}
            accessible={false}
            accessibilityLabel={`${tt(
              'stats.orderDate.label',
              'Order Date',
            )}: ${dateUtil.formatDate(orderDate, 'MMMM d, yyyy')}`}
            label={
              <Text testID={OrderDetails.orderDateLabel} accessible={true}>
                {tt('stats.orderDate.label', 'Order Date')}
              </Text>
            }
            suffix={
              <Text
                variant="button"
                testID={OrderDetails.orderDateValue}
                accessible={true}>
                {dateUtil.formatDate(orderDate, 'MMMM d, yyyy')}
              </Text>
            }
          />
          <ListItem
            testID={OrderDetails.subtotalConsumables}
            readonly
            style={{base: styles.statItem}}
            accessible={false}
            accessibilityLabel={`${tt(
              'stats.subtotalConsumables.label',
              'Subtotal Consumables',
            )}: ${getCurrencyAccessibilityValue(
              subtotalConsumableDisplayValue,
              orderTotalCurrencyCode,
            )}`}
            label={
              <Text
                testID={OrderDetails.subtotalConsumablesLabel}
                accessible={true}>
                {tt('stats.subtotalConsumables.label', 'Subtotal Consumables')}
              </Text>
            }
            suffix={
              <FormattedNumber
                testID={OrderDetails.subtotalConsumablesValue}
                value={subtotalConsumableDisplayValue}
                currencyCode={orderTotalCurrencyCode}
                separationStyle="thousand"
                decimalPlaces={2}
                accessible={true}
              />
            }
          />
          <ListItem
            testID={OrderDetails.orderTotal}
            readonly
            style={{base: styles.statItem}}
            accessible={false}
            accessibilityLabel={`${tt(
              'stats.orderTotal.label',
              'Order Total',
            )}: ${getCurrencyAccessibilityValue(
              orderTotalDisplayValue,
              orderTotalCurrencyCode,
            )}`}
            label={
              <Text testID={OrderDetails.orderTotalLabel} accessible={true}>
                {tt('stats.orderTotal.label', 'Order Total')}
              </Text>
            }
            suffix={
              <FormattedNumber
                testID={OrderDetails.orderTotalValue}
                value={orderTotalDisplayValue}
                currencyCode={orderTotalCurrencyCode}
                separationStyle="thousand"
                decimalPlaces={2}
                accessible={true}
              />
            }
          />
          <ListItem
            testID={OrderDetails.orderStatus}
            readonly
            style={{base: styles.statItem}}
            accessible={false}
            accessibilityLabel={`${tt(
              'stats.orderStatus.label',
              'Order Status',
            )}: ${orderStatusDisplayValue}`}
            label={
              <Text testID={OrderDetails.orderStatusLabel} accessible={true}>
                {tt('stats.orderStatus.label', 'Order Status')}
              </Text>
            }
            suffix={
              <Text
                variant="button"
                testID={OrderDetails.orderStatusValue}
                accessible={true}>
                {orderStatusDisplayValue}
              </Text>
            }
          />
        </View>
      </Gutter>
    </ScreenSection>
  );
};

export default withTranslation('screen.orderDetail.section.order')(
  OrderSection,
);
